import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import TermsAcceptance from "@/components/TermsAcceptance";
import { 
  Snowflake, 
  ArrowLeft, 
  Users, 
  Clock,
  User,
  Mail,
  Phone,
  Minus,
  Plus,
  CreditCard,
  CheckCircle2,
  CalendarDays
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const BASE_PRICE = 100.00;
const PER_PERSON_PRICE = 10.00;
const MAX_INCLUDED_PERSONS = 8;

// Season dates
const SEASON_START = new Date(2026, 10, 27); // Nov 27, 2026
const SEASON_END = new Date(2027, 0, 6); // Jan 6, 2027

const EisstockBooking = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [openingHours, setOpeningHours] = useState(null);
  
  // Default to season start month
  const [calendarMonth, setCalendarMonth] = useState(SEASON_START);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    groupSize: 4
  });

  // Base price includes up to 8 persons
  const extraPersons = Math.max(0, formData.groupSize - MAX_INCLUDED_PERSONS);
  const totalPrice = BASE_PRICE + extraPersons * PER_PERSON_PRICE;
  const SERVICE_FEE = 2.00;
  const grandTotal = totalPrice + SERVICE_FEE;

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    setOpeningHours(null);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/eisstock/slots/${dateStr}`);
      setAvailableSlots(response.data.slots || []);
      setOpeningHours(response.data.opening_hours || null);
      
      if (!response.data.in_season) {
        toast.error("Dieses Datum liegt außerhalb der Saison");
      }
    } catch (error) {
      toast.error("Fehler beim Laden der Zeitslots");
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const adjustGroupSize = (delta) => {
    setFormData(prev => ({
      ...prev,
      groupSize: Math.max(1, Math.min(20, prev.groupSize + delta))
    }));
  };

  const isDateDisabled = (date) => {
    return date < SEASON_START || date > SEASON_END || date < new Date();
  };

  const handleContinue = () => {
    if (!selectedDate || !selectedSlot) {
      toast.error("Bitte wähle Datum und Zeitslot");
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/eisstock/book`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        booking_date: format(selectedDate, "yyyy-MM-dd"),
        time_slot: selectedSlot,
        group_size: formData.groupSize
      });
      
      setBookingData(response.data);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Buchung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    try {
      const response = await axios.post(`${API}/paypal/create-order`, {
        booking_id: bookingData.id,
        booking_type: "eisstock"
      });
      return response.data.id;
    } catch (error) {
      toast.error("Fehler bei der Erstellung der PayPal-Bestellung");
      throw error;
    }
  };

  const onApprove = async (data) => {
    try {
      await axios.post(`${API}/paypal/capture-order`, {
        order_id: data.orderID,
        booking_id: bookingData.id,
        booking_type: "eisstock"
      });
      
      setBookingComplete(true);
      toast.success("Zahlung erfolgreich! Buchung bestätigt.");
    } catch (error) {
      toast.error("Fehler bei der Zahlungsabwicklung");
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full border-0 shadow-xl" data-testid="booking-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
              Buchung bestätigt!
            </h2>
            <p className="text-slate-600 mb-6">
              Vielen Dank für deine Buchung. Eine Bestätigung wurde an {formData.email} gesendet.
            </p>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Buchungsnummer</span>
                <span className="font-mono text-sm">{bookingData?.id?.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Datum</span>
                <span className="font-semibold">{selectedDate && format(selectedDate, "dd.MM.yyyy")}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Zeitslot</span>
                <span className="font-semibold">{selectedSlot}</span>
              </div>
              {bookingData?.lane && (
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500">Bahn</span>
                  <span className="font-semibold text-sky-600">Bahn {bookingData.lane}</span>
                </div>
              )}
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Gruppengröße</span>
                <span className="font-semibold">{formData.groupSize} Personen</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-slate-200">
                <span className="text-slate-500">Gesamtpreis</span>
                <span className="font-semibold text-sky-600">{grandTotal.toFixed(2)} €</span>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-green-800 text-sm">
                📧 Nach der Zahlung erhältst du dein Ticket per E-Mail mit QR-Code.<br />
                <strong>Bitte beim Einlass bereithalten (Handy oder Ausdruck).</strong>
              </p>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6 text-left">
              <p className="text-sky-900 text-sm">
                🥌 Noch nie Eisstockschießen gespielt?{" "}
                <Link
                  to="/eisstockregeln"
                  className="font-semibold text-sky-700 underline hover:text-sky-900"
                  data-testid="success-rules-link"
                >
                  Hier geht's zu den Spielregeln →
                </Link>
              </p>
              <p className="text-xs text-sky-700 mt-1">
                Die Regeln findest du auch auf Seite 2 deines PDF-Tickets.
              </p>
            </div>
            
            <Link to="/">
              <Button 
                className="w-full h-12 rounded-full bg-sky-500 hover:bg-sky-600"
                data-testid="back-home-btn"
              >
                Zurück zur Startseite
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-slate-600 hover:text-sky-500 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Zurück</span>
          </Link>
          <div className="flex items-center gap-3">
            <Snowflake className="w-6 h-6 text-sky-500" />
            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
              Grömitzer Eiszeit
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Image & Info */}
          <div className="lg:sticky lg:top-24 lg:h-fit">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-8">
              <img 
                src="https://images.pexels.com/photos/6220143/pexels-photo-6220143.jpeg?w=800&q=80" 
                alt="Eisstockschießen" 
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="inline-flex items-center gap-2 bg-sky-500 text-white rounded-full px-4 py-2 mb-4">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">1,5 Stunden pro Buchung</span>
                </div>
                <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  Eisstockschießen
                </h1>
                <p className="text-sky-100">
                  1 Bahn | bis zu 8 Personen | Perfekt für Familie, Freunde oder Firmenevents!
                </p>
              </div>
            </div>
            
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  Informationen:
                </h3>
                <ul className="space-y-3 text-slate-600">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-sky-500" />
                    1,5 Stunden Bahnmiete inklusive
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-sky-500" />
                    Bis zu 8 Personen im Preis enthalten
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-sky-500" />
                    Eisstöcke und Zielscheiben werden gestellt
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-sky-500" />
                    Kurze Einweisung vor Spielbeginn
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-sky-500" />
                    Perfekt für Firmenfeiern & Familien
                  </li>
                </ul>
                
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <h4 className="font-semibold text-slate-900 mb-2">Preise:</h4>
                  <div className="text-slate-600 text-sm space-y-1">
                    <p>Grundpreis (bis zu 8 Personen): <span className="font-semibold">{BASE_PRICE.toFixed(2)} €</span></p>
                    <p>Jede weitere Person: <span className="font-semibold">+{PER_PERSON_PRICE.toFixed(2)} €</span></p>
                  </div>
                  <p className="text-amber-600 text-sm mt-3 font-medium">
                    Buchung erforderlich! Zeitfenster werden nach Verfügbarkeit vergeben.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Booking Form */}
          <div>
            {/* Progress Steps */}
            <div className="flex items-center gap-2 mb-8">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-sky-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>
                  1
                </div>
                <span className="font-medium text-sm">Termin</span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200">
                <div className={`h-full bg-sky-500 transition-all ${step >= 2 ? 'w-full' : 'w-0'}`} />
              </div>
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-sky-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>
                  2
                </div>
                <span className="font-medium text-sm">Details</span>
              </div>
              <div className="flex-1 h-0.5 bg-slate-200">
                <div className={`h-full bg-sky-500 transition-all ${step >= 3 ? 'w-full' : 'w-0'}`} />
              </div>
              <div className={`flex items-center gap-2 ${step >= 3 ? 'text-sky-600' : 'text-slate-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 3 ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>
                  3
                </div>
                <span className="font-medium text-sm">Zahlung</span>
              </div>
            </div>

            {/* Step 1: Date & Time Selection */}
            {step === 1 && (
              <Card className="border-0 shadow-xl" data-testid="date-selection-card">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                    <CalendarDays className="w-6 h-6 inline-block mr-2 text-sky-500" />
                    Datum & Zeitslot wählen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Calendar */}
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={isDateDisabled}
                      locale={de}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      className="rounded-xl border border-slate-200"
                      data-testid="date-calendar"
                    />
                  </div>

                  {/* Time Slots */}
                  {selectedDate && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-slate-700">Verfügbare Zeitslots</Label>
                        {openingHours && (
                          <span className="text-sm text-sky-600 font-medium">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Öffnungszeiten: {openingHours}
                          </span>
                        )}
                      </div>
                      {loadingSlots ? (
                        <div className="text-center py-8 text-slate-500">
                          Lade Zeitslots...
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <div className="text-center py-8 text-amber-600 bg-amber-50 rounded-xl">
                          <p className="font-medium">Keine Zeitslots verfügbar</p>
                          <p className="text-sm mt-1">Bitte wähle ein anderes Datum innerhalb der Saison.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          {availableSlots.map((slot) => (
                            <Button
                              key={slot.time}
                              variant={selectedSlot === slot.time ? "default" : "outline"}
                              disabled={!slot.available}
                              onClick={() => setSelectedSlot(slot.time)}
                              className={`h-auto min-h-14 py-2 time-slot-btn flex-col ${
                                selectedSlot === slot.time 
                                  ? 'bg-sky-500 hover:bg-sky-600 text-white' 
                                  : 'hover:border-sky-300'
                              } ${!slot.available ? 'opacity-50' : ''}`}
                              data-testid={`slot-${slot.time.replace(':', '-')}`}
                            >
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-2" />
                                {slot.time}
                              </div>
                              {slot.available && slot.free_lanes && slot.free_lanes.length > 0 && (
                                <span className="text-xs opacity-75">
                                  {slot.free_lanes.length === slot.total_lanes
                                    ? `Alle ${slot.total_lanes} Bahnen frei`
                                    : `Bahn ${slot.free_lanes.join(', ')} frei`}
                                </span>
                              )}
                              {slot.available && (!slot.free_lanes || slot.free_lanes.length === 0) && slot.available_lanes !== undefined && (
                                <span className="text-xs opacity-75">
                                  {slot.available_lanes} von {slot.total_lanes} Bahnen frei
                                </span>
                              )}
                              {slot.booked && <span className="text-xs">(alle Bahnen belegt)</span>}
                              {slot.blocked && <span className="text-xs">(gesperrt)</span>}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Info */}
                  {selectedDate && selectedSlot && (
                    <div className="bg-sky-50 rounded-xl p-4 border border-sky-100">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-sky-600" />
                        <div>
                          <p className="font-semibold text-slate-900">
                            {format(selectedDate, "EEEE, dd. MMMM yyyy", { locale: de })}
                          </p>
                          <p className="text-slate-600">{selectedSlot} Uhr</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleContinue}
                    disabled={!selectedDate || !selectedSlot}
                    className="w-full h-14 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold text-lg shadow-lg shadow-sky-500/20"
                    data-testid="continue-to-details-btn"
                  >
                    Weiter zu den Details
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Contact Details */}
            {step === 2 && (
              <Card className="border-0 shadow-xl" data-testid="details-form-card">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                    Deine Daten
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Selected Date/Time Summary */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Gewählter Termin</p>
                        <p className="font-semibold text-slate-900">
                          {selectedDate && format(selectedDate, "dd.MM.yyyy")} | {selectedSlot}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setStep(1)}
                        className="text-sky-500"
                        data-testid="change-date-btn"
                      >
                        Ändern
                      </Button>
                    </div>
                  </div>

                  {/* Group Size */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <Label className="text-slate-700 mb-4 block">Gruppengröße</Label>
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustGroupSize(-1)}
                        disabled={formData.groupSize <= 1}
                        className="h-12 w-12 rounded-full"
                        data-testid="decrease-group-btn"
                      >
                        <Minus className="w-5 h-5" />
                      </Button>
                      <div className="text-center">
                        <span className="text-4xl font-bold text-slate-900" data-testid="group-size">
                          {formData.groupSize}
                        </span>
                        <p className="text-sm text-slate-500 mt-1">
                          {formData.groupSize === 1 ? 'Person' : 'Personen'}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustGroupSize(1)}
                        disabled={formData.groupSize >= 20}
                        className="h-12 w-12 rounded-full"
                        data-testid="increase-group-btn"
                      >
                        <Plus className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex justify-between text-sm text-slate-600 mb-1">
                        <span>Grundpreis (1,5h Bahn, bis 8 Pers.)</span>
                        <span>{BASE_PRICE.toFixed(2)} €</span>
                      </div>
                      {formData.groupSize > MAX_INCLUDED_PERSONS && (
                        <div className="flex justify-between text-sm text-slate-600 mb-1">
                          <span>{formData.groupSize - MAX_INCLUDED_PERSONS} weitere Person(en)</span>
                          <span>{((formData.groupSize - MAX_INCLUDED_PERSONS) * PER_PERSON_PRICE).toFixed(2)} €</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm text-slate-600 mb-1">
                        <span>Servicegebühr</span>
                        <span>{SERVICE_FEE.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-slate-200 mt-2">
                        <span className="font-semibold">Gesamt</span>
                        <span className="text-2xl font-bold text-sky-600" data-testid="total-price">
                          {grandTotal.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-700">Name</Label>
                      <div className="relative mt-2">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Max Mustermann"
                          className="h-12 pl-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20"
                          data-testid="name-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-slate-700">E-Mail</Label>
                      <div className="relative mt-2">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="max@example.de"
                          className="h-12 pl-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20"
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-slate-700">Telefon</Label>
                      <div className="relative mt-2">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+49 123 456789"
                          className="h-12 pl-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20"
                          data-testid="phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 h-12 rounded-full"
                      data-testid="back-to-date-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Zurück
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex-1 h-12 rounded-full bg-sky-500 hover:bg-sky-600"
                      data-testid="continue-to-payment-btn"
                    >
                      {loading ? "Wird verarbeitet..." : "Weiter zur Zahlung"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <Card className="border-0 shadow-xl" data-testid="payment-card">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                    Zahlung
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Order Summary */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <h4 className="font-semibold text-slate-900 mb-4">Zusammenfassung</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Datum</span>
                        <span className="font-medium">{selectedDate && format(selectedDate, "dd.MM.yyyy")}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Zeitslot</span>
                        <span className="font-medium">{selectedSlot}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Gruppengröße</span>
                        <span className="font-medium">{formData.groupSize} Personen</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Zwischensumme</span>
                        <span className="text-slate-700">{totalPrice.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Servicegebühr</span>
                        <span className="text-slate-700">{SERVICE_FEE.toFixed(2)} €</span>
                      </div>
                      <div className="border-t border-slate-200 pt-3 flex justify-between">
                        <span className="font-semibold text-slate-900">Gesamt</span>
                        <span className="text-2xl font-bold text-sky-600">{grandTotal.toFixed(2)} €</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">💳</span>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">Zahlungsarten</h4>
                        <p className="text-slate-700 font-medium">PayPal</p>
                        <p className="text-slate-600 text-sm mt-1">
                          (PayPal, Kreditkarte, Lastschrift oder Überweisung über PayPal)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* AGB Akzeptanz */}
                  <TermsAcceptance
                    checked={agbAccepted}
                    onChange={setAgbAccepted}
                    testId="eisstock-agb"
                  />

                  {/* PayPal Buttons */}
                  <div className="space-y-4">
                    {PAYPAL_CLIENT_ID && (
                      <div className={agbAccepted ? "" : "opacity-50 pointer-events-none"}>
                        <PayPalScriptProvider options={{ 
                          clientId: PAYPAL_CLIENT_ID,
                          currency: "EUR",
                          locale: "de_DE"
                        }}>
                          <PayPalButtons
                            style={{ 
                              layout: "vertical",
                              shape: "pill",
                              label: "pay"
                            }}
                            disabled={!agbAccepted}
                            createOrder={createOrder}
                            onApprove={onApprove}
                            onError={(err) => {
                              console.error("PayPal Error:", err);
                              toast.error("PayPal-Fehler. Bitte versuche es erneut.");
                            }}
                          />
                        </PayPalScriptProvider>
                      </div>
                    )}

                    {!agbAccepted && (
                      <p className="text-sm text-slate-500 text-center">
                        Bitte AGB akzeptieren, um die Zahlung fortzusetzen.
                      </p>
                    )}
                    
                    {/* Confirmation Info */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                      <p className="text-green-800 text-sm">
                        📧 Nach der Zahlung erhältst du dein Ticket per E-Mail mit QR-Code.<br />
                        <strong>Bitte beim Einlass bereithalten (Handy oder Ausdruck).</strong>
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    onClick={() => setStep(2)}
                    className="w-full"
                    data-testid="back-to-details-btn"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zurück zu den Details
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EisstockBooking;
